import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchAllBehaviorConfigs,
  fetchBehaviorConfig,
  createBehaviorConfig,
  updateBehaviorConfig,
} from "../actions/behaviorConfigActions";

// BehaviorConfig interface
export interface BehaviorConfig {
  id: string;
  behavior_name: string;
  demo_mode?: string | null;
  created_at: string;
  updated_at?: string | null;
}

// State interface
interface BehaviorConfigState {
  behaviorConfigs: BehaviorConfig[];
  currentBehaviorConfig: BehaviorConfig | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: BehaviorConfigState = {
  behaviorConfigs: [],
  currentBehaviorConfig: null,
  loading: false,
  error: null,
};

// Create the slice
const behaviorConfigSlice = createSlice({
  name: "behaviorConfigs",
  initialState,
  reducers: {
    clearCurrentBehaviorConfig(state) {
      state.currentBehaviorConfig = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<BehaviorConfigState>) => {
    // Handle fetchAllBehaviorConfigs
    builder
      .addCase(fetchAllBehaviorConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllBehaviorConfigs.fulfilled,
        (state, action: PayloadAction<BehaviorConfig[]>) => {
          state.loading = false;
          state.behaviorConfigs = action.payload;
        }
      )
      .addCase(fetchAllBehaviorConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch behavior configs";
      });

    // Handle fetchBehaviorConfig
    builder
      .addCase(fetchBehaviorConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBehaviorConfig.fulfilled,
        (state, action: PayloadAction<BehaviorConfig>) => {
          state.loading = false;
          state.currentBehaviorConfig = action.payload;
        }
      )
      .addCase(fetchBehaviorConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch behavior config";
      });

    // Handle createBehaviorConfig
    builder
      .addCase(createBehaviorConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createBehaviorConfig.fulfilled,
        (state, action: PayloadAction<BehaviorConfig>) => {
          state.loading = false;
          state.currentBehaviorConfig = action.payload;
          // Add the new config to the list
          state.behaviorConfigs.unshift(action.payload);
        }
      )
      .addCase(createBehaviorConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create behavior config";
      });

    // Handle updateBehaviorConfig
    builder
      .addCase(updateBehaviorConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateBehaviorConfig.fulfilled,
        (state, action: PayloadAction<BehaviorConfig>) => {
          state.loading = false;
          state.currentBehaviorConfig = action.payload;
          // Update the config in the list
          const index = state.behaviorConfigs.findIndex(
            (config) => config.id === action.payload.id
          );
          if (index !== -1) {
            state.behaviorConfigs[index] = action.payload;
          }
        }
      )
      .addCase(updateBehaviorConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update behavior config";
      });
  },
});

export const { clearCurrentBehaviorConfig } = behaviorConfigSlice.actions;
export default behaviorConfigSlice.reducer;
